import {
  Alert,
  Avatar,
  Box,
  IconButton,
  Menu,
  Skeleton,
  type AvatarProps
} from '@digibee/beehive-ui';
import { choose, iff, otherwise, when } from '@digibee/control-statements';
import {
  Camera,
  EditPencil,
  Lock,
  LogOut,
  SmartphoneDevice
} from 'iconoir-react';
import { memo, useCallback } from 'react';

import * as Elements from './Account.elements';
import { Props } from './Account.enhancer';

import { API_KEY } from '~/common/helpers/environment';
import i18n from '~/common/helpers/i18n';
import InputFile from '~/components/InputFile';

const Account = ({
  setModalNewPassword,
  setModalTwoFactor,
  setModalProfile,
  onSignOut,
  user,
  token,
  onChangeImage,
  avatarTimestamp,
  saveImageProfileLoading,
  isMainRealm
}: Props) => {
  const UserAvatar = useCallback(
    ({ size }: { size: AvatarProps['size'] }) => (
      <Avatar
        data-testid='profile-show-menu-button'
        size={size}
        text={user?.email}
        placeholder={user?.email}
        type={user?.imageUrl ? 'picture' : 'char'}
        config={{
          provider: {
            url: `${user?.imageUrl}?${avatarTimestamp}`,
            options: {
              responseType: 'blob',
              headers: {
                apiKey: API_KEY,
                Authorization: token
              }
            }
          }
        }}
      />
    ),
    [user, avatarTimestamp, token]
  );

  return (
    <Menu.Root modal={false}>
      <Elements.MenuContent
        componentTrigger={
          <IconButton>
            {choose(
              when(saveImageProfileLoading, () => (
                <Skeleton variant='circular' sizes='xLarge' />
              )),
              otherwise(() => <UserAvatar size='xsmall' />)
            )}
          </IconButton>
        }
      >
        <Elements.Profile>
          {choose(
            when(saveImageProfileLoading, () => (
              <Skeleton
                variant='circular'
                css={{
                  width: '80px',
                  height: '80px'
                }}
              />
            )),
            when(!isMainRealm, () => <UserAvatar size='xlarge' />),
            otherwise(() => (
              <InputFile
                data-testid='governance-photo-input-profile'
                onChange={onChangeImage}
                accept='image/png, image/jpg, image/jpeg'
              >
                <UserAvatar size='xlarge' />
                <Elements.Badge>
                  <Camera className='icon-smallest' />
                </Elements.Badge>
              </InputFile>
            ))
          )}
          {iff(!isMainRealm, () => (
            <Box
              // @ts-expect-error
              title={i18n.t('common.guidance.action_allowed_in_main_realm')}
              css={{ marginTop: '16px', marginBottom: '16px' }}
            >
              <Alert
                title={i18n.t('common.guidance.actions_disabled') || ''}
                variant='info'
                size='small'
              />
            </Box>
          ))}
          <Elements.Name
            as='span'
            className='fs-mask'
            data-testid='governance-username-profile'
          >
            {`${user?.firstName} ${user?.lastName}`}
          </Elements.Name>
          <Elements.Email
            as='span'
            className='fs-mask'
            data-testid='governance-email-profile'
          >
            {user?.email}
          </Elements.Email>
          <Elements.Timezone
            as='span'
            data-testid='governance-timezone-profile'
          >
            {user?.timezone}
          </Elements.Timezone>
        </Elements.Profile>
        <Elements.List inset noHairlines>
          <Elements.Item
            data-testid='profile-edit-button'
            onClick={() => setModalProfile(true)}
          >
            <div className='flex items-center gap-2 text-sm'>
              <EditPencil className='icon-smallest' />
              {i18n.t('scenes.profile.labels.edit_profile_details')}
            </div>
          </Elements.Item>
          {iff(isMainRealm, () => (
            <Elements.Item
              data-testid='profile-change-password-button'
              onClick={() => setModalNewPassword(true)}
            >
              <div className='flex items-center gap-2 text-sm'>
                <Lock className='icon-smallest' />
                {i18n.t('scenes.profile.labels.change_password')}
              </div>
            </Elements.Item>
          ))}
          {iff(isMainRealm, () => (
            <Elements.Item
              data-testid='profile-two-factor-auth-button'
              onClick={() => setModalTwoFactor(true)}
            >
              <div className='flex items-center gap-2 text-sm'>
                <SmartphoneDevice className='icon-smallest' />
                {i18n.t('scenes.profile.labels.two_factor')}
              </div>
            </Elements.Item>
          ))}
          <Elements.Item onClick={onSignOut} data-testid='profile-exit-button'>
            <div className='flex items-center gap-2 text-sm'>
              <LogOut className='icon-smallest' />
              {i18n.t('scenes.profile.labels.sign_out')}
            </div>
          </Elements.Item>
        </Elements.List>
      </Elements.MenuContent>
    </Menu.Root>
  );
};

export default memo(Account);
